/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { JobPresentation } from "@/presentation/Job";

export interface MetadataEvent {
  value: string | number;
  created: string;
  [key: string]: any;
}

//Check if an event timestamp falls within a job's time range
export function isEventInJobTimeRange(
  eventCreated: string,
  jobStart: string,
  jobEnd?: string
): boolean {
  // Validate input timestamps
  if (!eventCreated || !jobStart) return false;

  try {
    const eventTime = new Date(eventCreated).getTime();
    const jobStartTime = new Date(jobStart).getTime();

    // Check for invalid dates (NaN)
    if (isNaN(eventTime) || isNaN(jobStartTime)) return false;

    const jobEndTime = jobEnd ? new Date(jobEnd).getTime() : Date.now();

    // Check for invalid end date
    if (jobEnd && isNaN(jobEndTime)) return false;

    return eventTime >= jobStartTime && eventTime <= jobEndTime;
  } catch (error) {
    // Handle any parsing errors
    return false;
  }
}

//Flatten metadata filter values into a Set for O(1) lookup
export function createMetadataFilterSet(
  metadataFilters: Record<string, string[]>
): Set<string> {
  const filterSet = new Set<string>();
  Object.values(metadataFilters).forEach((values) => {
    values.forEach((value) => filterSet.add(value));
  });
  return filterSet;
}

//Filter metadata events to only those matching the filter values and within job time ranges
export function filterMetadataEvents(
  jobMetadata: MetadataEvent[],
  metadataFilterSet: Set<string>,
  jobs?: JobPresentation[]
): MetadataEvent[] {
  return jobMetadata.filter((event) => {
    if (event?.value === undefined || event?.value === null || !event?.created)
      return false;

    // Check if metadata value matches any filter
    if (!metadataFilterSet.has(String(event.value))) return false;

    // If jobs are provided, check if event falls within any job's time range
    if (jobs && jobs.length > 0) {
      return jobs.some((job) =>
        isEventInJobTimeRange(event.created, job.start, job.end)
      );
    }

    return true;
  });
}

export function createJobLookupCache(
  jobs: Mappy<JobPresentation>
): Map<string, JobPresentation> {
  const cache = new Map<string, JobPresentation>();
  Object.values(jobs).forEach((job) => {
    if (job?.name && job?.start) {
      cache.set(job.name, job);
    }
  });
  return cache;
}
